/**
 * Extensions of the rich-text editor (I-2799): tools another part of the
 * product plugs into every editor without the kit knowing them — the ERP's
 * text blocks, say. An extension is a button in the toolbar, optionally a
 * shortcut (F5), and a panel the toolbar opens beside the button. The panel
 * gets what the editor knows — the field's format, the selected text, the
 * placeholder values of the place it sits in — and inserts at the caret.
 *
 * Extensions reach the editors from above: the app provides them for all
 * editors (`provideRichTextExtensions`), a part of a page may add its own, an
 * editor may take more by prop. Placeholder values come the same way
 * (`provideRichTextVariables`), from the page that knows them, and so does
 * what the place is (`provideRichTextPlace`): a message, a note or a text, the
 * record it belongs to, and — where a message is built — a way to set its
 * subject, recipients and attachments, not only its text.
 */
import {
  computed,
  inject,
  provide,
  toValue,
  type Component,
  type ComputedRef,
  type InjectionKey,
  type MaybeRefOrGetter,
} from 'vue';
import type { NxOfficeIconName } from '../OfficeRibbon';
import { useRichTextKitExtensions } from './commentExtension';

/** What a field stores: HTML, or a Write document. */
export type NxRichTextFormat = 'html' | 'writez';

/** Placeholder values of the place an editor sits in (`customer.name` …). */
export type NxRichTextVariables = Readonly<Record<string, string | undefined>>;

/**
 * What a place an editor sits in is: the text of a message (a mail), of a note
 * (a note, a phone call) or any other text (a position, an article).
 */
export type NxRichTextPlaceKind = 'message' | 'note' | 'text';

/** The record a place belongs to, as its module names it (`contacts` 12). */
export interface NxRichTextRecord {
  type: string;
  id: number;
}

/** A whole message an extension puts in (a mail template): text and the rest. */
export interface NxRichTextMessage {
  /** The text, as HTML; it replaces the text there is. */
  html: string;
  subject?: string;
  /** Comma-separated addresses, added to those there are. */
  cc?: string;
  bcc?: string;
  attachments?: readonly File[];
  /**
   * Where the message came from, for a host that knows the extension: the
   * ERP's own B-DMS takes an ERP template by its id and checks it on saving.
   */
  origin?: Readonly<Record<string, unknown>>;
}

/** The place an editor sits in, from the page that knows it. */
export interface NxRichTextPlace {
  kind: NxRichTextPlaceKind;
  /** The record the text belongs to, when there is one. */
  record?: NxRichTextRecord | null;
  /** Whom a message goes to: what an extension can find a record by. */
  recipients?: readonly string[];
  /** Puts a whole message in; only a place that builds one has it. */
  applyMessage?: (message: NxRichTextMessage) => Promise<void> | void;
}

/** What an extension's panel gets from the editor it was opened in. */
export interface NxRichTextExtensionContext {
  format: NxRichTextFormat;
  /** The text selected when the panel opened, '' when none. */
  selectedText: string;
  variables: NxRichTextVariables;
  /** What the place is; null where no page said so. */
  place?: NxRichTextPlace | null;
  /** Inserts HTML at the caret (over the selection) as one undo step. */
  insert: (html: string) => Promise<void>;
  /** Closes the panel; the caret goes back into the text. */
  close: () => void;
}

export interface NxRichTextExtension {
  /** Unique, prefixed with the module that brings it (`erp.text_blocks`). */
  id: string;
  /** The button's text. */
  label: string;
  icon?: NxOfficeIconName;
  /** A key that opens the panel from the text: `F5`, `Mod+Shift+K`. */
  shortcut?: string;
  /** The formats it works in; all when unset. */
  formats?: readonly NxRichTextFormat[];
  /**
   * Extensions this one stands in for where both are offered: a place with a
   * picker of its own replaces the app-wide one (Mail's composer, which knows
   * the sender's signatures, replaces the ERP's).
   */
  replaces?: readonly string[];
  /** The panel the button opens; its one prop is `context`. */
  panel?: Component;
  /**
   * An extension without a panel: the button (or shortcut) does it at once —
   * the comment puts its text in. One of `panel` and `run` is set.
   */
  run?: (context: NxRichTextExtensionContext) => Promise<void> | void;
  /** Kinds of place it is offered in; every place when unset. */
  places?: readonly NxRichTextPlaceKind[];
  /**
   * Where the panel opens: a popover beside the button (default), or a window
   * of its own for a panel with much to show — thousands of text blocks.
   */
  presentation?: 'popover' | 'dialog';
}

type ExtensionList = readonly NxRichTextExtension[];

export const nxRichTextExtensionsKey: InjectionKey<ComputedRef<ExtensionList>> =
  Symbol('nxRichTextExtensions');
export const nxRichTextVariablesKey: InjectionKey<ComputedRef<NxRichTextVariables>> =
  Symbol('nxRichTextVariables');
export const nxRichTextPlaceKey: InjectionKey<ComputedRef<NxRichTextPlace | null>> =
  Symbol('nxRichTextPlace');

/**
 * Adds extensions for every editor below: on an app (`app.provide` through
 * `install`) or inside a component. What a parent provides stays; an id given
 * again replaces it.
 */
export function provideRichTextExtensions(extensions: MaybeRefOrGetter<ExtensionList>): void {
  const parent = inject(nxRichTextExtensionsKey, null);
  provide(
    nxRichTextExtensionsKey,
    computed(() => mergeExtensions(parent?.value ?? [], toValue(extensions)))
  );
}

/** Placeholder values for every editor below; the nearest provider wins per key. */
export function provideRichTextVariables(variables: MaybeRefOrGetter<NxRichTextVariables>): void {
  const parent = inject(nxRichTextVariablesKey, null);
  provide(
    nxRichTextVariablesKey,
    computed(() => ({ ...(parent?.value ?? {}), ...toValue(variables) }))
  );
}

/** What the place below is; the nearest provider wins. */
export function provideRichTextPlace(place: MaybeRefOrGetter<NxRichTextPlace | null>): void {
  provide(
    nxRichTextPlaceKey,
    computed(() => toValue(place))
  );
}

/** The place around an editor; null where no page said what it is. */
export function useRichTextPlace(): ComputedRef<NxRichTextPlace | null> {
  const provided = inject(nxRichTextPlaceKey, null);
  return computed(() => provided?.value ?? null);
}

/**
 * The extensions an editor of `format` offers: the kit's own (the comment),
 * the provided ones and its own — each only where its format and place fit.
 */
export function useRichTextExtensions(
  format: MaybeRefOrGetter<NxRichTextFormat>,
  own: MaybeRefOrGetter<ExtensionList | undefined> = undefined,
  /** The place, for a host that provides it itself (and so cannot inject it). */
  ownPlace: MaybeRefOrGetter<NxRichTextPlace | null | undefined> = undefined
): ComputedRef<ExtensionList> {
  const provided = inject(nxRichTextExtensionsKey, null);
  const place = useRichTextPlace();
  const kit = useRichTextKitExtensions();
  return computed(() => {
    const kind = (toValue(ownPlace) ?? place.value)?.kind ?? 'text';
    const offered = mergeExtensions(
      mergeExtensions(kit.value, provided?.value ?? []),
      toValue(own) ?? []
    ).filter(
      (extension) =>
        (!extension.formats || extension.formats.includes(toValue(format))) &&
        (!extension.places || extension.places.includes(kind))
    );
    const replaced = new Set(offered.flatMap((extension) => extension.replaces ?? []));
    return replaced.size ? offered.filter((extension) => !replaced.has(extension.id)) : offered;
  });
}

/** The placeholder values around an editor, with its own on top. */
export function useRichTextVariables(
  own: MaybeRefOrGetter<NxRichTextVariables | undefined> = undefined
): ComputedRef<NxRichTextVariables> {
  const provided = inject(nxRichTextVariablesKey, null);
  return computed(() => ({ ...(provided?.value ?? {}), ...(toValue(own) ?? {}) }));
}

function mergeExtensions(base: ExtensionList, added: ExtensionList): ExtensionList {
  if (!added.length) return base;
  const ids = new Set(added.map((extension) => extension.id));
  return [...base.filter((extension) => !ids.has(extension.id)), ...added];
}

/**
 * Whether a key event is an extension's shortcut. `Mod` is Ctrl, or Cmd on a
 * Mac; the parts are joined with `+`, the key last (`F5`, `Mod+Shift+K`).
 */
export function matchesRichTextShortcut(event: KeyboardEvent, shortcut: string): boolean {
  const parts = shortcut.split('+').map((part) => part.trim().toLowerCase());
  const key = parts.pop();
  if (!key) return false;
  const wants = new Set(parts);
  if (wants.has('mod')) {
    if (!event.ctrlKey && !event.metaKey) return false;
  } else if (wants.has('ctrl') !== event.ctrlKey || wants.has('meta') !== event.metaKey) {
    return false;
  }
  if (wants.has('shift') !== event.shiftKey) return false;
  if (wants.has('alt') !== event.altKey) return false;
  return event.key.toLowerCase() === key;
}

/** The extension a key event opens, if any. */
export function richTextExtensionForKey(
  extensions: ExtensionList,
  event: KeyboardEvent
): NxRichTextExtension | undefined {
  return extensions.find(
    (extension) => extension.shortcut && matchesRichTextShortcut(event, extension.shortcut)
  );
}
